import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faCalendarTimes,
  faCalendarPlus,
  faClock,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  createBlockedDay, 
  updateBlockedDay, 
  createBlockedDayRange 
} from '../../services/blockedDaysService';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import '../../styles/datepicker-custom.css';

const BlockDayForm = ({ blockedDay, onSubmit, onCancel }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    date: '',
    endDate: '', // For date ranges
    reason: '',
    allDay: true,
    startTime: '9:00 AM',
    endTime: '5:00 PM',
    location: 'All',
    isRange: false // Toggle for single day vs range
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form with blocked day data if editing
  useEffect(() => {
    if (blockedDay) {
      setFormData({
        date: blockedDay.date ? new Date(blockedDay.date) : '',
        endDate: '',
        reason: blockedDay.reason || '',
        allDay: blockedDay.allDay !== undefined ? blockedDay.allDay : true,
        startTime: blockedDay.startTime || '9:00 AM',
        endTime: blockedDay.endTime || '5:00 PM',
        location: blockedDay.location || 'All',
        isRange: false
      });
    }
  }, [blockedDay]);

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', 
    '6:00 PM', '7:00 PM'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear related errors
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (formData.isRange && !formData.endDate) {
      newErrors.endDate = 'End date is required for date range';
    }

    if (formData.isRange && formData.date && formData.endDate) {
      if (new Date(formData.endDate) < new Date(formData.date)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (!formData.allDay) {
      if (!formData.startTime) {
        newErrors.startTime = 'Start time is required';
      }
      if (!formData.endTime) {
        newErrors.endTime = 'End time is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const blockedDayData = {
        reason: formData.reason.trim(),
        allDay: formData.allDay,
        location: formData.location,
        createdBy: currentUser?.uid || 'admin'
      };

      // Add time fields if not all day
      if (!formData.allDay) {
        blockedDayData.startTime = formData.startTime;
        blockedDayData.endTime = formData.endTime;
      }

      if (blockedDay) {
        // Update existing blocked day
        await updateBlockedDay(blockedDay.id, {
          ...blockedDayData,
          date: formData.date.toISOString().split('T')[0]
        });
      } else {
        // Create new blocked day(s)
        if (formData.isRange && formData.endDate) {
          // Create range of blocked days
          await createBlockedDayRange(
            formData.date.toISOString().split('T')[0],
            formData.endDate.toISOString().split('T')[0],
            blockedDayData
          );
        } else {
          // Create single blocked day
          await createBlockedDay({
            ...blockedDayData,
            date: formData.date.toISOString().split('T')[0]
          });
        }
      }
      
      onSubmit();
      
    } catch (error) {
      console.error('Error saving blocked day:', error);
      setErrors({
        ...errors,
        submit: 'Failed to save blocked day. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    // Allow blocking past dates for admin purposes
    return null;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <style>
          {`
            /* Custom Radio Button Styles */
            .custom-radio-group {
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              margin-top: 0.5rem;
            }
            
            .custom-radio-option {
              display: flex;
              align-items: center;
              padding: 0.75rem 1rem;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s ease;
              background: white;
              position: relative;
            }
            
            .custom-radio-option:hover {
              border-color: #1E88E5;
              background-color: #f8fbff;
            }
            
            .custom-radio-option.selected {
              border-color: #1E88E5;
              background-color: #e3f2fd;
              box-shadow: 0 2px 8px rgba(30, 136, 229, 0.15);
            }
            
            .custom-radio-input {
              display: none;
            }
            
            .custom-radio-indicator {
              width: 20px;
              height: 20px;
              border: 2px solid #ccc;
              border-radius: 50%;
              margin-right: 0.75rem;
              position: relative;
              transition: all 0.2s ease;
              background: white;
            }
            
            .custom-radio-option.selected .custom-radio-indicator {
              border-color: #1E88E5;
              background-color: #1E88E5;
            }
            
            .custom-radio-indicator::after {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: white;
              opacity: 0;
              transition: opacity 0.2s ease;
            }
            
            .custom-radio-option.selected .custom-radio-indicator::after {
              opacity: 1;
            }
            
            .custom-radio-label {
              font-size: 0.9rem;
              font-weight: 500;
              color: #333;
              flex: 1;
            }
            
            .custom-radio-description {
              font-size: 0.8rem;
              color: #666;
              margin-top: 0.25rem;
            }
            
            /* Custom Checkbox Styles */
            .custom-checkbox-option {
              display: flex;
              align-items: center;
              padding: 0.75rem 1rem;
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s ease;
              background: white;
              margin-top: 0.5rem;
            }
            
            .custom-checkbox-option:hover {
              border-color: #1E88E5;
              background-color: #f8fbff;
            }
            
            .custom-checkbox-option.selected {
              border-color: #1E88E5;
              background-color: #e3f2fd;
            }
            
            .custom-checkbox-input {
              display: none;
            }
            
            .custom-checkbox-indicator {
              width: 20px;
              height: 20px;
              border: 2px solid #ccc;
              border-radius: 4px;
              margin-right: 0.75rem;
              position: relative;
              transition: all 0.2s ease;
              background: white;
            }
            
            .custom-checkbox-option.selected .custom-checkbox-indicator {
              border-color: #1E88E5;
              background-color: #1E88E5;
            }
            
            .custom-checkbox-indicator::after {
              content: '✓';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 12px;
              font-weight: bold;
              opacity: 0;
              transition: opacity 0.2s ease;
            }
            
            .custom-checkbox-option.selected .custom-checkbox-indicator::after {
              opacity: 1;
            }
            
            .custom-checkbox-label {
              font-size: 0.9rem;
              font-weight: 500;
              color: #333;
            }
          `}
        </style>
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={blockedDay ? faCalendarTimes : faCalendarPlus} />
            {blockedDay ? 'Edit Blocked Day' : 'Block Calendar Days'}
          </h3>
          <button 
            className="close-button" 
            onClick={onCancel}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {errors.submit && <div className="error-message">{errors.submit}</div>}
          {!blockedDay && (
            <div className="form-row">
              <div className="form-group">
                <label>Date Selection Type</label>
                <div 
                  className={`custom-checkbox-option ${formData.isRange ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, isRange: !prev.isRange }))}
                >
                  <input
                    type="checkbox"
                    className="custom-checkbox-input"
                    name="isRange"
                    checked={formData.isRange}
                    onChange={handleChange}
                  />
                  <div className="custom-checkbox-indicator"></div>
                  <div>
                    <div className="custom-checkbox-label">Block date range (multiple days)</div>
                    <div className="custom-radio-description">Select a start and end date to block multiple consecutive days</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">
                {formData.isRange ? 'Start Date*' : 'Date*'}
              </label>
              <DatePicker
                selected={formData.date}
                onChange={(date) => handleDateChange(date, 'date')}
                minDate={getMinDate()}
                className={errors.date ? 'error' : ''}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
              />
              {errors.date && <div className="error-message">{errors.date}</div>}
            </div>
            
            {formData.isRange && (
              <div className="form-group">
                <label htmlFor="endDate">End Date*</label>
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date) => handleDateChange(date, 'endDate')}
                  minDate={formData.date}
                  className={errors.endDate ? 'error' : ''}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select end date"
                />
                {errors.endDate && <div className="error-message">{errors.endDate}</div>}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="reason">Reason for Blocking*</label>
              <input
                type="text"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                className={errors.reason ? 'error' : ''}
                placeholder="e.g., Maintenance, Holiday, Equipment Repair"
              />
              {errors.reason && <div className="error-message">{errors.reason}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="location">
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Location
              </label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
              >
                <option value="All">All Locations</option>
                <option value="Angola">Angola Office Only</option>
                <option value="Elkhart">Elkhart Office Only</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <FontAwesomeIcon icon={faClock} /> Time Blocking Options
              </label>
              <div className="custom-radio-group">
                <div 
                  className={`custom-radio-option ${formData.allDay ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, allDay: true }))}
                >
                  <input
                    type="radio"
                    className="custom-radio-input"
                    name="allDay"
                    checked={formData.allDay}
                    onChange={() => setFormData(prev => ({ ...prev, allDay: true }))}
                  />
                  <div className="custom-radio-indicator"></div>
                  <div>
                    <div className="custom-radio-label">Block entire day</div>
                    <div className="custom-radio-description">Prevent all bookings for the selected date(s)</div>
                  </div>
                </div>
                
                <div 
                  className={`custom-radio-option ${!formData.allDay ? 'selected' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, allDay: false }))}
                >
                  <input
                    type="radio"
                    className="custom-radio-input"
                    name="allDay"
                    checked={!formData.allDay}
                    onChange={() => setFormData(prev => ({ ...prev, allDay: false }))}
                  />
                  <div className="custom-radio-indicator"></div>
                  <div>
                    <div className="custom-radio-label">Block specific hours only</div>
                    <div className="custom-radio-description">Block only during specified time periods</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!formData.allDay && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime">Start Time*</label>
                <select
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  className={errors.startTime ? 'error' : ''}
                >
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                {errors.startTime && <div className="error-message">{errors.startTime}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time*</label>
                <select
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  className={errors.endTime ? 'error' : ''}
                >
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                {errors.endTime && <div className="error-message">{errors.endTime}</div>}
              </div>
            </div>
          )}

          {formData.isRange && formData.date && formData.endDate && (
            <div style={{
              backgroundColor: '#e3f2fd',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              padding: '12px',
              marginTop: '1rem',
              fontSize: '0.9rem'
            }}>
              <strong>Note:</strong> This will block {
                Math.ceil((new Date(formData.endDate) - new Date(formData.date)) / (1000 * 60 * 60 * 24)) + 1
              } days from {formData.date.toLocaleDateString()} to {formData.endDate.toLocaleDateString()}.
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
            paddingTop: '1.5rem',
            marginTop: '1.5rem',
            borderTop: '1px solid #e0e0e0'
          }}>
            <button 
              type="button"
              className="secondary-button" 
              onClick={onCancel}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} /> Cancel
            </button>
            <button 
              type="submit"
              className="primary-button"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} /> 
              {loading ? 'Saving...' : (blockedDay ? 'Update' : 'Block Days')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlockDayForm;